import { useEffect, useMemo, useRef, useState } from "react";

import { Navigate, useLocation, useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import FloatSemanticMessage from "components/FloatSemanticMessage";

import BasicLayout from "layouts/authentication/components/BasicLayout";
import { subscribeToAuthChanges } from "services/firebase";
import {
  exchangeTrayAuthorizationCode,
  redirectToTrayAuthorization,
  storeTrayStoreHost,
} from "services/tray";

import { canShowAdmin, getCategorySession } from "services/categories";

import bgImage from "assets/images/bg-sign-in-basic.jpeg";

function TrayCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const [authenticatedUser, setAuthenticatedUser] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState("");
  const [isStartingAuthorization, setIsStartingAuthorization] = useState(false);
  const [isAdminVerified, setIsAdminVerified] = useState(false);
  const exchangedCodes = useRef(new Set());

  const trayParams = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      apiAddress: params.get("api_address"),
      code: params.get("code"),
      store: params.get("store"),
      storeHost: params.get("store_host") || params.get("url"),
    };
  }, [location.search]);

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((user) => {
      setAuthenticatedUser(user);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!authenticatedUser) return undefined;
    let active = true;
    if (!canShowAdmin(authenticatedUser)) {
      navigate("/dashboard", { replace: true });
      return undefined;
    }
    getCategorySession()
      .then((session) => {
        if (active && session.isAdmin) setIsAdminVerified(true);
        else if (active) navigate("/dashboard", { replace: true });
      })
      .catch((error) => {
        if (active) setErrorMessage(error.message);
      });
    return () => {
      active = false;
    };
  }, [authenticatedUser, navigate]);

  useEffect(() => {
    if (trayParams.storeHost) {
      storeTrayStoreHost(trayParams.storeHost);
    }
  }, [trayParams.storeHost]);

  useEffect(() => {
    const finishTrayAuthorization = async () => {
      if (!isAdminVerified || !trayParams.code || !trayParams.apiAddress) return;
      const exchangeKey = `${trayParams.apiAddress}:${trayParams.code}`;
      if (exchangedCodes.current.has(exchangeKey)) return;
      exchangedCodes.current.add(exchangeKey);

      try {
        await exchangeTrayAuthorizationCode(trayParams);
        navigate("/admin?section=tray", { replace: true });
      } catch (error) {
        setErrorMessage(error?.message || "Nao foi possivel autenticar com a loja.");
      }
    };

    finishTrayAuthorization();
  }, [isAdminVerified, navigate, trayParams]);

  const handleStartAuthorization = async () => {
    if (!isAdminVerified) return;
    setErrorMessage("");
    setIsStartingAuthorization(true);

    try {
      const redirected = await redirectToTrayAuthorization();

      if (!redirected) {
        setErrorMessage("Informe a loja e as credenciais da aplicacao para continuar.");
      }
    } catch (error) {
      setErrorMessage(error?.message || "Nao foi possivel iniciar a autorizacao da loja.");
    } finally {
      setIsStartingAuthorization(false);
    }
  };

  if (authenticatedUser === undefined) {
    return null;
  }

  if (!authenticatedUser) {
    return <Navigate to="/login" replace />;
  }

  if (!canShowAdmin(authenticatedUser)) return <Navigate to="/dashboard" replace />;
  if (!isAdminVerified && !errorMessage)
    return <CircularProgress aria-label="Verificando administração" />;

  const hasAuthorizationCode = Boolean(trayParams.code && trayParams.apiAddress);

  return (
    <>
      <FloatSemanticMessage
        open={Boolean(errorMessage)}
        title="Falha na conexão da loja"
        message={errorMessage || ""}
        severity="error"
        onClose={() => setErrorMessage("")}
      />
      <BasicLayout image={bgImage}>
        <Card>
          <MDBox p={4} textAlign="center">
            <MDBox mb={2}>
              <MDTypography variant="h4" fontWeight="medium">
                Conexão da loja
              </MDTypography>
            </MDBox>
            <MDBox mb={3}>
              <MDTypography variant="body2" color="text">
                {hasAuthorizationCode
                  ? "Finalizando autenticacao com a loja."
                  : "Conexão administrativa da loja. Os autores usam suas contas PSO."}
              </MDTypography>
            </MDBox>
            {hasAuthorizationCode ? (
              <CircularProgress size={28} color="info" />
            ) : (
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleStartAuthorization}
                disabled={isStartingAuthorization || !isAdminVerified}
              >
                {isStartingAuthorization ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Autorizar loja"
                )}
              </MDButton>
            )}
          </MDBox>
        </Card>
      </BasicLayout>
    </>
  );
}

export default TrayCallback;
